import { supabase } from './supabase';

export interface FlatRateCategory {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface FlatRateItem {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  category_id: string | null;
  hours: number;
  order_index: number;
  created_at: string;
  updated_at: string;
  category?: FlatRateCategory;
}

interface BookingFlatRate {
  id: string;
  booking_id: string;
  item_id: string;
  resource_id: string;
  hours: number;
  custom_hours?: number;
  notes: string | null;
  completed_at?: string | null;
  completed_by?: string | null;
  created_at: string;
  updated_at: string;
  item_name?: string;
  item_description?: string;
  item_hours?: number;
  resource_first_name?: string;
  resource_last_name?: string;
  resource_email?: string;
}

export async function getFlatRateItems(businessId: string) {
  const { data, error } = await supabase
    .from('flat_rate_items')
    .select(`
      *,
      category:flat_rate_categories(*)
    `)
    .eq('business_id', businessId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data as FlatRateItem[];
}

export async function getFlatRateCategories(businessId: string) {
  const { data, error } = await supabase
    .from('flat_rate_categories')
    .select('*')
    .eq('business_id', businessId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data as FlatRateCategory[];
}

export async function createFlatRateCategory(category: Omit<FlatRateCategory, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('flat_rate_categories')
    .insert([category])
    .select()
    .single();

  if (error) throw error;
  return data as FlatRateCategory;
}

async function updateFlatRateCategory(id: string, updates: Partial<FlatRateCategory>) {
  const { data, error } = await supabase
    .from('flat_rate_categories')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as FlatRateCategory;
}

async function deleteFlatRateCategory(id: string) {
  const { error } = await supabase
    .from('flat_rate_categories')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function createFlatRateItem(item: Omit<FlatRateItem, 'id' | 'created_at' | 'updated_at'>) {
  if (!item.business_id) {
    throw new Error('Unable to create item: Business ID is required');
  }

  // Validate hours
  if (isNaN(item.hours) || item.hours <= 0) {
    throw new Error('Hours must be greater than 0');
  }

  // Ensure category_id is properly handled
  const category_id = item.category_id && item.category_id.trim() !== '' 
    ? item.category_id 
    : null;

  const { data, error } = await supabase
    .from('flat_rate_items')
    .insert([{
      ...item,
      hours: parseFloat(item.hours.toString()),
      category_id
    }])
    .select()
    .single();

  if (error) throw error;
  return data as FlatRateItem;
}

export async function updateFlatRateItem(id: string, updates: Partial<FlatRateItem>) {
  const { data, error } = await supabase
    .from('flat_rate_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as FlatRateItem;
}

export async function deleteFlatRateItem(id: string) {
  const { error } = await supabase
    .from('flat_rate_items')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

async function getBookingFlatRates(bookingId: string) {
  const { data, error } = await supabase
    .from('booking_flat_rates_with_details')
    .select('*')
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data as BookingFlatRate[];
}

async function createBookingFlatRate(flatRate: Omit<BookingFlatRate, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('booking_flat_rates')
    .insert([flatRate])
    .select()
    .single();

  if (error) throw error;
  return data as BookingFlatRate;
}

async function updateBookingFlatRate(id: string, updates: Partial<BookingFlatRate>) {
  const { data, error } = await supabase
    .from('booking_flat_rates')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as BookingFlatRate;
}

async function deleteBookingFlatRate(id: string) {
  const { error } = await supabase
    .from('booking_flat_rates')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Mark flat rate items as completed for a resource
export async function markFlatRateItemsCompleted(
  bookingId: string,
  resourceId: string
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log('Marking flat rate items as completed:', { bookingId, resourceId });

  const { error } = await supabase.rpc('mark_flat_rate_items_completed', {
    p_booking_id: bookingId,
    p_resource_id: resourceId
  });

  if (error) {
    console.error('Error marking flat rate items as completed:', error);
    throw error;
  }

  console.log('Successfully marked flat rate items as completed');
}

// Check for incomplete flat rate groups in a booking
export async function getIncompleteFlatRateGroups(bookingId: string): Promise<number> {
  if (!supabase) throw new Error('Supabase client not initialized');

  try {
    console.log('Checking for incomplete flat rate groups in booking:', bookingId);

    const { data, error } = await supabase
      .from('booking_flat_rates')
      .select('resource_id, completed_at')
      .eq('booking_id', bookingId);

    if (error) {
      console.error('Error fetching flat rate groups:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.log('No flat rate items found in booking');
      return 0;
    }

    // Group by resource_id and check if any group has incomplete items
    const resourceGroups = data.reduce((groups: Record<string, any[]>, item) => {
      if (!groups[item.resource_id]) {
        groups[item.resource_id] = [];
      }
      groups[item.resource_id].push(item);
      return groups;
    }, {});

    let incompleteGroups = 0;
    for (const [resourceId, items] of Object.entries(resourceGroups)) {
      // A group is incomplete if any item in the group has no completed_at timestamp
      const hasIncompleteItems = items.some(item => !item.completed_at);
      if (hasIncompleteItems) {
        incompleteGroups++;
        console.log(`Resource ${resourceId} has incomplete flat rate items`);
      }
    }

    console.log(`Found ${incompleteGroups} incomplete flat rate groups`);
    return incompleteGroups;
  } catch (error) {
    console.error('Error checking incomplete flat rate groups:', error);
    throw error;
  }
}

// Complete all flat rate groups in a booking
export async function completeAllFlatRateGroups(bookingId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  try {
    console.log('Completing all flat rate groups in booking:', bookingId);

    // Get all distinct resource IDs with incomplete items
    const { data: incompleteItems, error: fetchError } = await supabase
      .from('booking_flat_rates')
      .select('resource_id')
      .eq('booking_id', bookingId)
      .is('completed_at', null);

    if (fetchError) {
      console.error('Error fetching incomplete flat rate items:', fetchError);
      throw fetchError;
    }

    if (!incompleteItems || incompleteItems.length === 0) {
      console.log('No incomplete flat rate items found');
      return;
    }

    // Get unique resource IDs
    const uniqueResourceIds = [...new Set(incompleteItems.map(item => item.resource_id))];
    console.log(`Completing flat rate groups for ${uniqueResourceIds.length} resources`);

    // Complete each resource group
    for (const resourceId of uniqueResourceIds) {
      await markFlatRateItemsCompleted(bookingId, resourceId);
    }

    console.log('All flat rate groups completed successfully');
  } catch (error) {
    console.error('Error completing all flat rate groups:', error);
    throw error;
  }
}